<template>
    <div class="ui-page">
        <ui-page-header title="Homologação" subtitle="Registre a decisão do juízo sobre o resultado de cada item. Só entram itens com o termo de proposta final assinado. Homologado, o ativo fica vendido e o nome do vencedor aparece no resultado público (R2).">
            <v-btn v-if="processId && pending.length && $allowed('competitive.homologation')" color="primary" depressed @click="openForm"><v-icon left small>mdi-gavel</v-icon>Registrar decisão</v-btn>
        </ui-page-header>

        <ui-table-card>
            <template #toolbar>
                <competitive-process-picker v-model="processId" />
                <span class="ui-toolbar__spacer"></span>
                <span v-if="data" class="ui-small ui-muted">{{ pending.length }} item(ns) aguardando decisão</span>
            </template>
            <div v-if="!processId" class="ui-small ui-muted pa-4">Selecione um processo.</div>
            <div v-else-if="data && !pending.length" class="ui-small ui-muted pa-4">Nenhum item aguardando decisão. Os itens entram aqui quando o vencedor assina o termo de proposta final.</div>
            <v-simple-table v-else-if="data" dense>
                <thead><tr><th>Etapa</th><th>Item</th><th>Vencedor</th><th class="text-right">Valor</th><th>Pagamento</th><th>Termo assinado</th></tr></thead>
                <tbody>
                    <tr v-for="item in pending" :key="item.result_id">
                        <td>{{ item.stage_number }}</td>
                        <td><strong>{{ item.code }}</strong> · {{ item.name }}</td>
                        <td>{{ item.winner }}<div class="ui-cell-sub">{{ item.registration_protocol }}<span v-if="item.position > 1"> · {{ item.position }}º convocado</span></div></td>
                        <td class="text-right ui-nowrap">{{ formatMoney(item.value) }}</td>
                        <td class="ui-small">{{ item.payment || "—" }}</td>
                        <td class="ui-small ui-nowrap">{{ formatStamp(item.signed_at) }} · <a href="#" @click.prevent="openSignature(item.signature_id)">PDF</a></td>
                    </tr>
                </tbody>
            </v-simple-table>
        </ui-table-card>

        <ui-section v-for="decision in homologations" :key="decision.id" :title="`Decisão de ${formatDate(decision.decision_date)} · Etapa ${decision.stage_number}`" :description="decision.case_reference || 'Sem referência dos autos'" class="mt-4">
            <div class="d-flex align-center flex-wrap mb-2" style="gap: 8px;">
                <v-btn small outlined @click="openDecision(decision)"><v-icon left small>mdi-file-pdf-box</v-icon>{{ decision.file_name }}</v-btn>
                <span class="ui-small ui-mono" :title="decision.file_hash">SHA-256 {{ decision.file_hash.slice(0, 16) }}…</span>
                <span class="ui-small ui-muted">registrada em {{ formatStamp(decision.created_at) }}</span>
            </div>
            <div v-if="decision.notes" class="ui-small mb-2">{{ decision.notes }}</div>
            <v-simple-table dense>
                <thead><tr><th>Item</th><th>Vencedor</th><th class="text-right">Valor</th><th>Decisão</th></tr></thead>
                <tbody>
                    <tr v-for="item in decision.items" :key="item.result_id">
                        <td><strong>{{ item.code }}</strong> · {{ item.name }}</td>
                        <td>{{ item.winner }}</td>
                        <td class="text-right ui-nowrap">{{ formatMoney(item.value) }}</td>
                        <td>
                            <ui-badge :text="item.homologation_label" :tone="item.homologation_status === 'homologated' ? 'success' : 'danger'" />
                            <div v-if="item.homologation_note" class="ui-cell-sub">{{ item.homologation_note }}</div>
                        </td>
                    </tr>
                </tbody>
            </v-simple-table>
        </ui-section>

        <v-dialog v-model="form.open" max-width="720" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header"><h3 class="ui-section-title">Registrar decisão do juízo</h3></div>
                <div class="ui-card__body">
                    <v-row dense>
                        <v-col cols="4"><v-select v-model="form.stage_id" :items="stageOptions" label="Etapa" outlined dense hide-details="auto" :menu-props="{ offsetY: true }" @change="resetItems"></v-select></v-col>
                        <v-col cols="4"><v-text-field v-model="form.decision_date" type="date" :max="today" label="Data da decisão" outlined dense hide-details="auto"></v-text-field></v-col>
                        <v-col cols="4"><v-text-field v-model="form.case_reference" label="Referência nos autos" placeholder="Nº do processo, evento" outlined dense hide-details="auto" maxlength="120"></v-text-field></v-col>
                        <v-col cols="12"><v-textarea v-model="form.notes" label="Observações (opcional)" outlined dense rows="2" auto-grow hide-details="auto" maxlength="2000"></v-textarea></v-col>
                    </v-row>

                    <div class="ui-small ui-muted mt-3 mb-1">Marque os itens que a decisão alcança e o que o juízo decidiu em cada um.</div>
                    <v-simple-table dense class="mb-3">
                        <thead><tr><th style="width: 36px;"></th><th>Item</th><th class="text-right">Valor</th><th style="width: 260px;">Decisão</th></tr></thead>
                        <tbody>
                            <tr v-for="item in formItems" :key="item.result_id">
                                <td><v-simple-checkbox v-model="item.selected" :ripple="false"></v-simple-checkbox></td>
                                <td><strong>{{ item.code }}</strong> · {{ item.name }}<div class="ui-cell-sub">{{ item.winner }}</div></td>
                                <td class="text-right ui-nowrap">{{ formatMoney(item.value) }}</td>
                                <td class="py-1">
                                    <v-btn-toggle v-model="item.decision" mandatory dense :disabled="!item.selected">
                                        <v-btn small value="homologated">Homologado</v-btn>
                                        <v-btn small value="rejected">Não homologado</v-btn>
                                    </v-btn-toggle>
                                    <v-text-field v-if="item.selected && item.decision === 'rejected'" v-model="item.note" label="Motivo" outlined dense hide-details="auto" class="mt-1" maxlength="500"></v-text-field>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>

                    <ui-file-drop :file="form.file" accept="application/pdf" :max-size-mb="200" hint="Decisão do juízo em PDF" :loading="form.saving"
                        @select="form.file = $event" @error="(message) => notifyWarning(message)" />
                    <v-progress-linear v-if="form.saving && form.file" :value="form.progress" height="4" rounded color="primary" class="mt-2"></v-progress-linear>
                    <div class="ui-small ui-muted mt-2">O registro é definitivo e entra na trilha com carimbo do tempo. Os vencedores recebem a decisão por e-mail.</div>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="form.saving" @click="form.open = false">Cancelar</v-btn>
                    <v-btn color="primary" depressed :loading="form.saving" :disabled="!canSave" @click="save">Registrar</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import CompetitiveProcessPicker from "@/components/competitive/CompetitiveProcessPicker.vue";
import processScope from "@/components/competitive/process-scope";
import { API, formatMoney, formatStamp, notifyError, notifySuccess, notifyWarning, openUrl, uploadForRecord } from "@/components/competitive/competitive.util";

const localToday = () => {
    const now = new Date();
    return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

export default {
    name: "CompetitiveHomologation",
    components: { CompetitiveProcessPicker },
    mixins: [processScope],
    data: () => ({
        data: null,
        loading: false,
        today: localToday(),
        form: { open: false, saving: false, progress: 0, file: null, stage_id: null, decision_date: "", case_reference: "", notes: "" },
        formItems: [],
    }),
    computed: {
        pending() {
            return this.data ? this.data.pending : [];
        },
        homologations() {
            return this.data ? this.data.homologations : [];
        },
        stageOptions() {
            const numbers = [...new Map(this.pending.map((item) => [item.stage_id, item.stage_number])).entries()];
            return numbers.map(([value, number]) => ({ value, text: `Etapa ${number}` }));
        },
        canSave() {
            const chosen = this.formItems.filter((item) => item.selected);
            return !!this.form.stage_id && !!this.form.decision_date && !!this.form.file && chosen.length > 0
                && chosen.every((item) => item.decision === "homologated" || String(item.note || "").trim());
        },
    },
    methods: {
        formatMoney,
        formatStamp,
        notifyWarning(message) {
            notifyWarning(this, message);
        },
        formatDate(value) {
            const [y, m, d] = String(value || "").split("-");
            return d ? `${d}/${m}/${y}` : "—";
        },
        onProcess() {
            this.data = null;
            this.load();
        },
        async load() {
            if (!this.processId) return;
            this.loading = true;
            try {
                const result = await axios.get(`${API}/processes/${this.processId}/homologations`);
                this.data = result.data.content;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },
        openForm() {
            this.form = { open: true, saving: false, progress: 0, file: null, stage_id: this.stageOptions.length ? this.stageOptions[0].value : null, decision_date: "", case_reference: "", notes: "" };
            this.resetItems();
        },
        resetItems() {
            this.formItems = this.pending.filter((item) => item.stage_id === this.form.stage_id)
                .map((item) => ({ ...item, selected: true, decision: "homologated", note: "" }));
        },
        async save() {
            this.form.saving = true;
            try {
                const file = await uploadForRecord(this.form.file, (percent) => { this.form.progress = percent; });
                const items = this.formItems.filter((item) => item.selected).map((item) => ({ result_id: item.result_id, decision: item.decision, note: item.decision === "rejected" ? item.note : null }));
                const result = await axios.post(`${API}/processes/${this.processId}/homologations`, {
                    stage_id: this.form.stage_id,
                    decision_date: this.form.decision_date,
                    case_reference: this.form.case_reference,
                    notes: this.form.notes,
                    items,
                    ...file,
                });
                this.data = result.data.content;
                this.form.open = false;
                notifySuccess(this, "Decisão registrada.");
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.form.saving = false;
            }
        },
        async openDecision(decision) {
            try {
                const result = await axios.get(`${API}/homologations/${decision.id}/file`);
                openUrl(result.data.content.url);
            } catch (error) {
                notifyError(this, error);
            }
        },
        async openSignature(id) {
            try {
                const result = await axios.get(`${API}/signatures/${id}/file`);
                openUrl(result.data.content.url);
            } catch (error) {
                notifyError(this, error);
            }
        },
    },
};
</script>

<style lang="scss">
@import "../../components/competitive/competitive";
</style>
